import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// project imports
import { API_URL } from 'config';
import { credentialsUpdated, loggedOut } from 'features/auth/authSlice';

// Requests to these paths must never trigger the reauth flow below, or a bad
// login attempt / an already-rotated refresh token would recurse forever.
const NO_REAUTH_PATHS = ['auth/login', 'auth/refresh'];

function requestUrl(args) {
  return typeof args === 'string' ? args : args.url;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

// Concurrent 401s must share a single refresh call instead of each firing their own.
let refreshPromise = null;

async function baseQueryWithReauth(args, api, extraOptions) {
  let result = await rawBaseQuery(args, api, extraOptions);

  const shouldReauth = result.error?.status === 401 && !NO_REAUTH_PATHS.includes(requestUrl(args));

  if (shouldReauth) {
    const refreshToken = api.getState().auth.refreshToken;

    if (!refreshToken) {
      api.dispatch(loggedOut());
      return result;
    }

    if (!refreshPromise) {
      refreshPromise = rawBaseQuery({ url: 'auth/refresh', method: 'POST', body: { refreshToken } }, api, extraOptions).finally(() => {
        refreshPromise = null;
      });
    }

    const refreshResult = await refreshPromise;

    if (refreshResult.data) {
      api.dispatch(credentialsUpdated(refreshResult.data));
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(loggedOut());
    }
  }

  return result;
}

// ==============================|| API - BASE SLICE ||============================== //
// One RTK Query slice for the whole app; every module injects its own endpoints
// into this via injectEndpoints() instead of creating a separate store slice.

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Me',
    'Usuario',
    'Rol',
    'Permiso',
    'Cliente',
    'Ruta',
    'RutaClientes',
    'Prestamo',
    'Cuota',
    'Recaudo',
    'Caja',
    'Trabajador',
    'Dispositivo',
    'Bloqueo',
    'RegistroActividad',
    'TipoMovimiento'
  ],
  endpoints: () => ({})
});
